import React, { useState, useEffect } from 'react';
import { Modal, Form, Alert, Spinner, Card, Dropdown } from 'react-bootstrap';
import { BsFileEarmarkText, BsChevronRight, BsPlusCircle, BsArrowRepeat, BsTrash } from 'react-icons/bs';
import { useWorkspace } from '../../../Context/WorkspaceContext';
import './AddNotionModal.css';
import '../agent-training.css';
import { CancelButton, PrimaryButton, OutlinedPrimaryButton, OutlinedSecondaryButton } from '../../../Components/Buttons';
import { useLanguage } from '../../../Context/LanguageContext';


const AddNotionModal = ({ show, onHide, workspaceId, apiUrl, onPollState }) => {
  const { t } = useLanguage();
  const { currentWorkspace } = useWorkspace();
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [error, setError] = useState(null);
  const [needsNotionAuth, setNeedsNotionAuth] = useState(false);
  const [pages, setPages] = useState([]);
  const [selectedPages, setSelectedPages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [connectedWorkspace, setConnectedWorkspace] = useState(null);
  const [accountsLoaded, setAccountsLoaded] = useState(false);

  // Add confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);

  // Check for connected workspace when modal opens
  useEffect(() => {
    if (show) {
      // First check if there's a workspace stored in localStorage from redirect
      const storedWorkspace = localStorage.getItem('temp_connected_workspace');
      if (storedWorkspace) {
        setConnectedWorkspace(storedWorkspace);
        // Remove it once read
        localStorage.removeItem('temp_connected_workspace');
      }

      // As a fallback, also check URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const workspaceParam = urlParams.get('connected_workspace');
      if (workspaceParam && !storedWorkspace) {
        setConnectedWorkspace(workspaceParam);
      }

      // Reset accountsLoaded flag when modal reopens
      setAccountsLoaded(false);
      fetchConnectedAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // This effect runs when accounts are loaded or connectedWorkspace changes
  useEffect(() => {
    // Skip if accounts aren't loaded yet or if we don't have any accounts
    if (!accountsLoaded || connectedAccounts.length === 0) {
      return;
    }

    // If we have a connected workspace, look for a matching account
    if (connectedWorkspace) {
      const matchingAccount = connectedAccounts.find(
        account => account.workspace_name === connectedWorkspace
      );

      if (matchingAccount) {
        // Set a timeout to ensure we don't select too quickly
        setTimeout(() => {
          setCurrentAccount(matchingAccount);
        }, 300);
      } else {
        setCurrentAccount(connectedAccounts[0]);
      }

      // Clear connected workspace after using it
      setConnectedWorkspace(null);
    } else if (!currentAccount && connectedAccounts.length > 0) {
      // If no connected workspace but we have accounts, select the first one
      setCurrentAccount(connectedAccounts[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountsLoaded, connectedAccounts, connectedWorkspace]);

  // Fetch pages when currentAccount changes
  useEffect(() => {
    if (currentAccount) {
      fetchPages(currentAccount.account_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccount]);

  // Fetch connected Notion accounts
  const fetchConnectedAccounts = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');
      // Get workspace_id from WorkspaceContext
      const workspace_id = currentWorkspace?.workspace_id;

      if (!workspace_id) {
        setError(t('dashboard.training.modals.addnotionmodal.errorNoWorkspace'));
        setNeedsNotionAuth(true);
        return;
      }

      const response = await fetch(`${apiUrl}/api/v1/notion/accounts?workspace_id=${workspace_id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${brandvoxToken}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        const accounts = data.accounts || [];
        setConnectedAccounts(accounts);

        if (accounts.length > 0) {
          setNeedsNotionAuth(false);
        } else {
          setNeedsNotionAuth(true);
          setCurrentAccount(null);
        }

        // Set accountsLoaded to true to trigger the selection effect
        setAccountsLoaded(true);
      } else {
        // Handle validation errors properly
        const errorMessage = typeof data.detail === 'string' ? data.detail :
          Array.isArray(data.detail) ? data.detail.map(err => err.msg).join(', ') :
            t('dashboard.training.modals.addnotionmodal.errorFetchAccounts');
        setError(errorMessage);
        setNeedsNotionAuth(true);
      }
    } catch (err) {
      setError(err.message || t('dashboard.training.modals.addnotionmodal.errorFetchAccounts'));
      setNeedsNotionAuth(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch Notion pages list for a specific account
  const fetchPages = async (accountId) => {
    setIsLoading(true);
    setError(null);
    setPages([]);

    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');
      // Get workspace_id from WorkspaceContext
      const workspace_id = currentWorkspace?.workspace_id;

      if (!workspace_id) {
        setError(t('dashboard.training.modals.addnotionmodal.errorNoWorkspace'));
        return;
      }

      const response = await fetch(`${apiUrl}/api/v1/notion/pages/list`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          account_id: accountId,
          workspace_id: workspace_id
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setPages(data.pages || []);
      } else if (data.needs_notion_auth) {
        setNeedsNotionAuth(true);
      } else {
        setError(data.detail || t('dashboard.training.modals.addnotionmodal.errorFetchPages'));
      }
    } catch (err) {
      setError(t('dashboard.training.modals.addnotionmodal.errorFetchPages'));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle page selection
  const togglePageSelection = (pageId) => {
    setSelectedPages((prevSelected) => {
      if (prevSelected.includes(pageId)) {
        return prevSelected.filter((id) => id !== pageId);
      } else {
        return [...prevSelected, pageId];
      }
    });
  };

  // Handle Notion Auth
  const handleNotionAuth = () => {
    setIsAuthenticating(true);
    // Store current URL for returning after auth
    localStorage.setItem('notionAuthRedirectUrl', window.location.href);
    // Get workspace_id from context
    const workspace_id = currentWorkspace?.workspace_id;
    const brandvoxToken = localStorage.getItem('brandvox_token');

    // Create the auth request parameters
    const authParams = new URLSearchParams({
      state: `add_account:${workspaceId}`,
      workspace_id: workspace_id,
      brandvox_token: brandvoxToken
    });

    // Redirect to new OAuth endpoint
    window.location.href = `${apiUrl}/api/v1/notion/auth/authorize?${authParams.toString()}`;
  };

  // Switch Notion account
  const handleAccountSwitch = (account) => {
    setCurrentAccount(account);
    setSelectedPages([]);
  };

  // Handle Notion Account Revocation
  const handleRevokeAccount = async (account) => {
    showConfirm('revokeAccount', account);
  };

  const handleRevokeAccountConfirmed = async (account) => {
    setIsRevoking(true);
    setError(null);

    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');
      // Get workspace_id from WorkspaceContext
      const workspace_id = currentWorkspace?.workspace_id;

      const response = await fetch(`${apiUrl}/api/v1/notion/accounts/revoke`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          account_id: account.account_id,
          workspace_id: workspace_id
        }),
      });

      if (response.ok) {
        // Refresh the accounts list
        await fetchConnectedAccounts();

        // If the revoked account was the current one, switch to another account or show auth screen
        if (currentAccount && currentAccount.account_id === account.account_id) {
          if (connectedAccounts.length > 1) {
            // Find an account that's not the revoked one
            const nextAccount = connectedAccounts.find(acc => acc.account_id !== account.account_id);
            if (nextAccount) {
              setCurrentAccount(nextAccount);
              fetchPages(nextAccount.account_id);
            } else {
              setNeedsNotionAuth(true);
            }
          } else {
            setNeedsNotionAuth(true);
          }
        }
      } else {
        const data = await response.json();
        setError(data.detail || t('dashboard.training.modals.addnotionmodal.errorRevokeAccount'));
      }
    } catch (err) {
      setError(t('dashboard.training.modals.addnotionmodal.errorRevokeAccount'));
    } finally {
      setIsRevoking(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedPages.length === 0) {
      setError(t('dashboard.training.modals.addnotionmodal.errorSelectPage'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');

      const response = await fetch(`${apiUrl}/api/v1/notion/pages/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workspace_id: workspaceId,
          account_id: currentAccount.account_id,
          page_ids: selectedPages
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || t('dashboard.training.modals.addnotionmodal.errorAddPages'));
      }

      onPollState();
      onHide();
    } catch (err) {
      setError(err.message || t('dashboard.training.modals.addnotionmodal.errorAddPages'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter pages based on search term
  const filteredPages = pages.filter(page =>
    page?.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Render page item
  const renderPageItem = (page) => {
    const isSelected = selectedPages.includes(page.id);
    const formattedDate = new Date(page.last_edited_time).toLocaleDateString();

    return (
      <Card
        key={page.id}
        className={`mb-2 page-card ${isSelected ? 'selected' : ''}`}
        onClick={() => togglePageSelection(page.id)}
      >
        <Card.Body className="d-flex align-items-center p-3">
          <div className="me-1">
            <Form.Check
              type="checkbox"
              checked={isSelected}
              onChange={() => { }}
              onClick={(e) => e.stopPropagation()}
              className="page-checkbox"
              aria-label={`Select ${page.title}`}
            />
          </div>
          <div className="me-2">
            {page.icon && page.icon.type === 'emoji' ? (
              <span style={{ fontSize: '24px' }}>{page.icon.emoji}</span>
            ) : (
              <BsFileEarmarkText size={24} className="text-primary" />
            )}
          </div>
          <div className="flex-grow-1">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">{page.title || t('dashboard.training.modals.addnotionmodal.untitled')}</h6>
              <small className="text-muted">{formattedDate}</small>
            </div>
            <small className="text-muted d-block mt-1">
              <a href={page.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                {t('dashboard.training.modals.addnotionmodal.viewInNotion')}
              </a>
            </small>
          </div>
        </Card.Body>
      </Card>
    );
  };

  // Render account selector
  const renderAccountSelector = () => {
    if (connectedAccounts.length === 0) return null;

    return (
      <div className="mb-3 d-flex justify-content-between align-items-center account-selector-container">
        <div className="d-flex align-items-center">
          <Dropdown>
            <Dropdown.Toggle variant="outline-secondary" id="account-dropdown" className="d-flex align-items-center account-toggle">
              <span className="text-truncate workspace-name">{currentAccount?.workspace_name}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu className="account-dropdown-menu">
              {connectedAccounts.map(account => (
                <div key={account.account_id} className="account-item px-2 py-1">
                  <div className="d-flex justify-content-between align-items-center">
                    <Dropdown.Item
                      onClick={() => handleAccountSwitch(account)}
                      active={currentAccount?.account_id === account.account_id}
                      className="d-flex align-items-center account-select-item"
                    >
                      <span className="text-truncate">{account.workspace_name}</span>
                    </Dropdown.Item>
                    <button
                      type="button"
                      onClick={() => handleRevokeAccount(account)}
                      className="btn btn-sm btn-danger disconnect-btn ms-2"
                      disabled={isRevoking}
                      title={t('dashboard.training.modals.addnotionmodal.disconnectButton')}
                    >
                      {isRevoking && currentAccount?.account_id === account.account_id ? (
                        <Spinner size="sm" />
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16" className="bi bi-trash">
                          <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z" />
                          <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              ))}
              <Dropdown.Divider />
              <Dropdown.Item onClick={handleNotionAuth} className="d-flex align-items-center">
                <BsPlusCircle className="me-2" />
                {t('dashboard.training.modals.addnotionmodal.addAnotherAccount')}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          <OutlinedSecondaryButton
            size="sm"
            className="ms-2"
            onClick={() => fetchPages(currentAccount.account_id)}
            title={t('dashboard.training.modals.addnotionmodal.refreshButton')}
          >
            <BsArrowRepeat />
          </OutlinedSecondaryButton>
        </div>
        <div className="pages-count-container">
          <small className="text-muted">
            {t('dashboard.training.modals.addnotionmodal.pagesAvailable', { count: pages.length })}
          </small>
        </div>
      </div>
    );
  };

  // Notion icon component
  const NotionIcon = ({ size = 48 }) => (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.017 4.313l55.333 -4.087c6.797 -0.583 8.543 -0.19 12.817 2.917l17.663 12.443c2.913 2.14 3.883 2.723 3.883 5.053v68.243c0 4.277 -1.553 6.807 -6.99 7.193L24.467 99.967c-4.08 0.193 -6.023 -0.39 -8.16 -3.113L3.3 79.94c-2.333 -3.113 -3.3 -5.443 -3.3 -8.167V11.113c0 -3.497 1.553 -6.413 6.017 -6.8z" fill="#fff" />
      <path d="M61.35 0.227l-55.333 4.087C1.553 4.7 0 7.617 0 11.113v60.66c0 2.723 0.967 5.053 3.3 8.167l13.007 16.913c2.137 2.723 4.08 3.307 8.16 3.113l64.257 -3.89c5.433 -0.387 6.99 -2.917 6.99 -7.193V20.64c0 -2.21 -0.873 -2.847 -3.443 -4.733L74.167 3.143c-4.273 -3.107 -6.02 -3.5 -12.817 -2.917zM25.92 19.523c-5.247 0.353 -6.437 0.433 -9.417 -1.99L8.927 11.507c-0.77 -0.78 -0.383 -1.753 1.557 -1.947l53.193 -3.887c4.467 -0.39 6.793 1.167 8.54 2.527l9.123 6.61c0.39 0.197 1.36 1.36 0.193 1.36l-54.933 3.307 -0.68 0.047zM19.803 88.3V30.367c0 -2.53 0.777 -3.697 3.103 -3.893L86 22.78c2.14 -0.193 3.107 1.167 3.107 3.693v57.547c0 2.53 -0.39 4.67 -3.883 4.863l-60.377 3.5c-3.493 0.193 -5.043 -0.97 -5.043 -4.083zm59.6 -54.827c0.387 1.75 0 3.5 -1.75 3.7l-2.91 0.577v42.773c-2.527 1.36 -4.853 2.137 -6.797 2.137 -3.107 0 -3.883 -0.973 -6.21 -3.887l-19.03 -29.94v28.967l6.02 1.363s0 3.5 -4.857 3.5l-13.39 0.777c-0.39 -0.78 0 -2.723 1.357 -3.11l3.497 -0.97v-38.3L30.48 40.667c-0.39 -1.75 0.58 -4.277 3.3 -4.473l14.367 -0.967 19.8 30.327v-26.83l-5.047 -0.58c-0.39 -2.143 1.163 -3.7 3.103 -3.89l13.4 -0.78z" fill="#000" />
    </svg>
  );

  // Function to show confirmation modal
  const showConfirm = (action, data) => {
    setConfirmAction(action);
    setConfirmData(data);
    setShowConfirmModal(true);
  };

  // Get confirmation message based on action
  const getConfirmMessage = () => {
    switch (confirmAction) {
      case 'revokeAccount':
        return t('dashboard.training.modals.addnotionmodal.confirmDisconnectMessage');
      default:
        return '';
    }
  };

  // Handle confirmation
  const handleConfirm = async () => {
    setShowConfirmModal(false);

    switch (confirmAction) {
      case 'revokeAccount':
        await handleRevokeAccountConfirmed(confirmData);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered className="add-notion-modal">
        <Modal.Header closeButton>
          <Modal.Title>{t('dashboard.training.modals.addnotionmodal.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}

          {needsNotionAuth ? (
            <div className="text-center py-4">
              <NotionIcon size={48} />
              <h5 className="mt-3">{t('dashboard.training.modals.addnotionmodal.connectTitle')}</h5>
              <p className="text-muted mb-4">
                {t('dashboard.training.modals.addnotionmodal.connectDescription')}
              </p>
              <PrimaryButton
                backgroundColor="var(--notion-primary)"
                color="white"
                hoverBackgroundColor="var(--notion-primary)"
                hoverColor="white"
                hoverShadow="none"
                width="100%"
                onClick={handleNotionAuth}
                disabled={isAuthenticating}
              >
                {isAuthenticating ? (
                  <Spinner size="sm" />
                ) : (
                  <NotionIcon size={16} />
                )}
                <span className="ms-2">{t('dashboard.training.modals.addnotionmodal.connectButton')}</span>
              </PrimaryButton>
            </div>
          ) : (
            <Form onSubmit={handleSubmit}>
              {renderAccountSelector()}

              <Form.Group className="mb-3">
                <Form.Control
                  type="text"
                  placeholder={t('dashboard.training.modals.addnotionmodal.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Form.Group>

              {isLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                  <p className="mt-2">{t('dashboard.training.modals.addnotionmodal.loadingPages')}</p>
                </div>
              ) : pages.length === 0 ? (
                <div className="text-center py-4">
                  <p>{t('dashboard.training.modals.addnotionmodal.noPagesAvailable')}</p>
                </div>
              ) : (
                <div className="pages-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {filteredPages.length > 0 ? (
                    filteredPages.map(renderPageItem)
                  ) : (
                    <p className="text-center py-3">{t('dashboard.training.modals.addnotionmodal.noMatchingPages')}</p>
                  )}
                </div>
              )}

              <div className="d-flex justify-content-between mt-3">
                <div>
                  <small className="text-muted">
                    {selectedPages.length > 0
                      ? <span>{t('dashboard.training.modals.addnotionmodal.pagesSelected', { count: selectedPages.length })}</span>
                      : <span>{t('dashboard.training.modals.addnotionmodal.selectPagesHint')}</span>}
                  </small>
                </div>
                <OutlinedPrimaryButton
                  className="hover-lift clear-selection-btn"
                  onClick={() => setSelectedPages([])}
                  disabled={selectedPages.length === 0}
                >
                  {t('dashboard.training.modals.addnotionmodal.clearSelectionButton')}
                </OutlinedPrimaryButton>
              </div>
            </Form>
          )}
        </Modal.Body>
        {!needsNotionAuth && (
          <Modal.Footer>
            <CancelButton variant="secondary" className="cancel-btn" onClick={onHide}>
              {t('dashboard.training.modals.addnotionmodal.cancelButton')}
            </CancelButton>
            <PrimaryButton
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting || selectedPages.length === 0}
            >
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {t('dashboard.training.modals.addnotionmodal.addingButton')}
                </>
              ) : (
                <>
                  {t('dashboard.training.modals.addnotionmodal.addButton')}
                  <BsChevronRight className="ms-2" />
                </>
              )}
            </PrimaryButton>
          </Modal.Footer>
        )}
      </Modal>

      {/* Confirmation modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
        backdrop="static"
        keyboard={false}
        contentClassName="border-0 shadow-lg"
      >
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="fw-bold text-danger">
            {t('dashboard.training.modals.addnotionmodal.confirmDisconnectTitle')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          <div className="d-flex">
            <div className="me-3 text-danger">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z" />
              </svg>
            </div>
            <p className="mb-0">{getConfirmMessage()}</p>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top-0">
          <CancelButton
            variant="lighter"
            onClick={() => setShowConfirmModal(false)}
            className="me-2 hover-lift cancel-btn"
          >
            {t('dashboard.training.modals.addnotionmodal.cancelButton')}
          </CancelButton>
          <PrimaryButton
            variant="danger"
            onClick={handleConfirm}
            className="px-4 hover-lift"
          >
            <span className="d-flex align-items-center">
              <BsTrash className="me-2" />
              {t('dashboard.training.modals.addnotionmodal.disconnectButton')}
            </span>
          </PrimaryButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AddNotionModal; 